<script setup lang="ts">
// The model a task runs on. Empty means the enterprise default. Typing filters the provider directory;
// Enter selects the highlighted model and must not submit the task form this field sits in.
// The search is the kit `Input`, the results sit on a kit surface and each result is a kit `Button` (#1227): the
// highlighted one takes the kit's own secondary treatment rather than a fill stated here.
// #1755: models read by the portal's mode. Standard shows each model's name and calls the unset default "Recommended";
// Advanced shows the exact ID. Search matches on ID and name in both, so a pasted ID still finds its model.

import { Button, Input, ZhPanel } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import type { LlmDirectoryModel } from '@/api'
import { useUiMode } from '@/composables/useUiMode'
import { modelOptionLabel, UNKNOWN_MODEL_LABEL, UNLISTED_MODEL_LABEL, useLlmModels } from '@/lib/llmModels'

export type ModelTierDraft = { model: string }

/** `bare` leaves out the field's own "Model" heading, for a form whose shared field (#1756) already labels it. */
const props = defineProps<{ form: ModelTierDraft; bare?: boolean }>()

const { mode } = useUiMode()
const {
    models,
    failed,
    findModel,
    loadLlmModels,
    recommendedLabel,
    recommendedModelLabel,
    selectedModelLabel,
    settled,
} = useLlmModels()
const query = ref('')
const editing = ref(false)
const open = ref(false)
const activeIndex = ref(0)
/** How many matches to paint. The directory is hundreds of rows; the rest stay one more keystroke away. */
const SHOWN_LIMIT = 40

onMounted(loadLlmModels)

/** Lower is a closer match. 99 means the row does not match the query at all. */
function score(model: LlmDirectoryModel, needle: string): number {
    if (!needle) {
        return 0
    }
    const id = model.id.toLowerCase()
    const name = model.name.toLowerCase()
    if (id === needle || name === needle) {
        return 0
    }
    if (id.startsWith(needle) || name.startsWith(needle)) {
        return 1
    }
    if (id.includes(needle) || name.includes(needle)) {
        return 2
    }
    return 99
}

const matches = computed(() => {
    const needle = query.value.trim().toLowerCase()
    return models.value
        .map((model, index) => ({ model, index, score: score(model, needle) }))
        .filter(row => row.score < 99)
        .sort((a, b) => a.score - b.score || a.index - b.index)
        .map(row => row.model)
})

const shown = computed(() => matches.value.slice(0, SHOWN_LIMIT))

/** What the field reads when not being typed in: Recommended for the unset default, else the saved model. */
const currentLabel = computed(() =>
    props.form.model ? selectedModelLabel(props.form.model, mode.value) : recommendedLabel(mode.value),
)

/** The line under the field, in the same words as the field: never an ID in Standard. */
const currentHint = computed(() => {
    if (!props.form.model) {
        const resolved = recommendedModelLabel(mode.value)
        return resolved ? `Using the recommended model, ${resolved}.` : 'Using the recommended model.'
    }
    if (!settled.value || findModel(props.form.model)) {
        return `Using ${currentLabel.value}.`
    }
    if (failed.value) {
        // The list could not be read, so nobody knows whether the providers still offer it: say so, not "unlisted".
        return mode.value === 'advanced'
            ? `Using ${props.form.model}. The model list could not be loaded, so the saved model is kept.`
            : `${UNKNOWN_MODEL_LABEL}: the model list could not be loaded. It is kept until you pick another.`
    }
    // A saved model the list does not offer now is kept as it is, never dropped, until another is picked.
    return mode.value === 'advanced'
        ? `Using ${props.form.model}, which the connected providers do not list now.`
        : `${UNLISTED_MODEL_LABEL}: the connected providers do not list it now. It is kept until you pick another.`
})

function optionLabel(model: LlmDirectoryModel): string {
    return modelOptionLabel(model, mode.value)
}

function startEdit(): void {
    editing.value = true
    open.value = true
    query.value = ''
    activeIndex.value = 0
}

function onInput(event: Event): void {
    editing.value = true
    open.value = true
    query.value = (event.target as HTMLInputElement).value
    activeIndex.value = 0
}

function choose(id: string): void {
    props.form.model = id
    query.value = ''
    editing.value = false
    open.value = false
}

function chooseHighlighted(): void {
    const needle = query.value.trim()
    if (!needle) {
        return
    }
    const pick = shown.value[activeIndex.value] ?? shown.value[0]
    if (pick) {
        choose(pick.id)
    }
}

function move(step: number): void {
    open.value = true
    const count = shown.value.length
    if (count === 0) {
        return
    }
    activeIndex.value = (activeIndex.value + step + count) % count
}

function stopEdit(): void {
    // A result is chosen on mousedown, which arrives before this blur. Wait so that click still lands.
    window.setTimeout(() => {
        editing.value = false
        open.value = false
        query.value = ''
    }, 120)
}
</script>

<template>
	<div class="relative text-sm">
		<span v-if="!bare" class="block">Model</span>
		<Input
			:model-value="editing ? query : currentLabel"
			type="text"
			role="combobox"
			aria-autocomplete="list"
			:aria-expanded="open"
			data-testid="model-search"
			placeholder="Search the directory"
			autocomplete="off"
			class="mt-1"
			@focus="startEdit"
			@input="onInput"
			@keydown.enter.stop.prevent="chooseHighlighted"
			@keydown.down.stop.prevent="move(1)"
			@keydown.up.stop.prevent="move(-1)"
			@keydown.esc="open = false"
			@blur="stopEdit"
		/>
		<p data-testid="model-current" class="text-muted-foreground mt-1 text-xs">
			{{ currentHint }}
			Type to narrow the directory. Enter selects the highlighted model and does not save the task.
		</p>
		<ZhPanel
			v-if="open"
			as="ul"
			flush
			data-testid="model-results"
			role="listbox"
			class="absolute z-20 mt-1 max-h-60 w-full overflow-auto shadow"
		>
			<li>
				<Button
					type="button"
					variant="ghost"
					class="h-auto w-full justify-start py-1 text-left whitespace-normal"
					data-testid="model-option-default"
					@mousedown.prevent="choose('')"
				>
					{{ recommendedLabel(mode) }}
				</Button>
			</li>
			<li v-for="(model, index) in shown" :key="model.id">
				<Button
					type="button"
					:variant="index === activeIndex ? 'secondary' : 'ghost'"
					class="h-auto w-full justify-start py-1 text-left whitespace-normal"
					data-testid="model-option"
					role="option"
					:aria-selected="index === activeIndex"
					@mousedown.prevent="choose(model.id)"
				>
					{{ optionLabel(model) }}
				</Button>
			</li>
			<li v-if="query.trim() && shown.length === 0" class="text-muted-foreground px-2 py-1 text-xs">No models match.</li>
			<li v-else-if="matches.length > shown.length" class="text-muted-foreground px-2 py-1 text-xs">
				{{ matches.length - shown.length }} more. Keep typing to narrow the list.
			</li>
		</ZhPanel>
	</div>
</template>
